import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';

const Nav = styled.nav`
  background: linear-gradient(135deg, #731c28 0%, #8c2332 55%, #7d1f2c 100%);
  color: #fff;
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  backdrop-filter: blur(6px);
`;

const NavInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  min-height: 72px;
  padding: 0 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
`;

const Logo = styled(Link)`
  font-size: 1.12rem;
  font-weight: 800;
  color: #fff;
  letter-spacing: 0.2px;
  z-index: 3;
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.18);

  &:hover {
    opacity: 0.96;
  }
`;

const shimmer = keyframes`
  0% { transform: translateX(-30px) rotate(18deg); opacity: 0; }
  30% { opacity: 0.35; }
  100% { transform: translateX(55px) rotate(18deg); opacity: 0; }
`;

const bubbleRise = keyframes`
  0% {
    transform: translateY(0) scale(0.9);
    opacity: 0;
  }
  20% {
    opacity: 0.45;
  }
  100% {
    transform: translateY(-16px) scale(1.15);
    opacity: 0;
  }
`;

const pourOut = keyframes`
  0% {
    transform: scaleX(0.15);
    opacity: 0;
  }
  100% {
    transform: scaleX(1);
    opacity: 1;
  }
`;

const ButtonReset = css`
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  appearance: none;
`;

const MenuButton = styled.button`
  ${ButtonReset}
  display: none;
  cursor: pointer;
  color: #fff;
  z-index: 3;
  position: relative;

  @media (max-width: 768px) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 62px;
    height: 62px;
  }
`;

const IconWrap = styled.div`
  position: relative;
  width: 42px;
  height: 58px;
  transition:
    transform 0.55s cubic-bezier(0.22, 1, 0.36, 1),
    filter 0.3s ease;
  transform-origin: 55% 82%;
  filter: drop-shadow(0 6px 14px rgba(0, 0, 0, 0.22));

  ${({ $open }) =>
    $open &&
    css`
      transform: rotate(96deg) translate(2px, 4px) scale(1.02);
    `}
`;

const IconSvg = styled.svg`
  width: 100%;
  height: 100%;
  display: block;
  overflow: visible;
`;

const GlassOutline = styled.path`
  fill: rgba(255, 255, 255, 0.04);
  stroke: rgba(255, 255, 255, 0.96);
  stroke-width: 2.2;
  stroke-linejoin: round;
`;

const Stem = styled.rect`
  fill: rgba(255, 255, 255, 0.96);
`;

const Base = styled.ellipse`
  fill: rgba(255, 255, 255, 0.96);
`;

const LiquidClip = styled.clipPath``;

const LiquidShape = styled.rect`
  fill: url(#spritzerLiquid);
  transition: y 0.45s ease, height 0.45s ease, opacity 0.3s ease;
  opacity: ${({ $open }) => ($open ? 0.7 : 1)};
`;

const LiquidTop = styled.ellipse`
  fill: rgba(240, 255, 235, 0.68);
  transition: cy 0.45s ease, opacity 0.35s ease;
  opacity: ${({ $open }) => ($open ? 0.35 : 0.95)};
`;

const GlassShine = styled.div`
  position: absolute;
  top: 8px;
  left: 10px;
  width: 12px;
  height: 30px;
  border-radius: 999px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.68) 0%,
    rgba(255, 255, 255, 0.1) 80%,
    rgba(255, 255, 255, 0) 100%
  );
  transform: rotate(8deg);
  pointer-events: none;
`;

const ShineSweep = styled.div`
  position: absolute;
  top: 7px;
  left: 5px;
  width: 10px;
  height: 40px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.26) 45%,
    rgba(255, 255, 255, 0) 100%
  );
  filter: blur(1px);
  pointer-events: none;
  animation: ${shimmer} 2.8s ease-in-out infinite;

  ${({ $open }) =>
    $open &&
    css`
      animation: none;
      opacity: 0.12;
    `}
`;

const Bubble = styled.span`
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.82);
  width: ${({ $size }) => $size};
  height: ${({ $size }) => $size};
  left: ${({ $left }) => $left};
  bottom: ${({ $bottom }) => $bottom};
  animation: ${bubbleRise} ${({ $duration }) => $duration} ease-in infinite;
  animation-delay: ${({ $delay }) => $delay};
  pointer-events: none;

  ${({ $open }) =>
    $open &&
    css`
      display: none;
    `}
`;

const Splash = styled.div`
  position: absolute;
  right: -16px;
  bottom: 10px;
  width: 0;
  height: 7px;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    rgba(217, 245, 199, 0.95) 0%,
    rgba(168, 220, 150, 0.92) 100%
  );
  transform-origin: left center;
  opacity: 0;
  pointer-events: none;

  ${({ $open }) =>
    $open &&
    css`
      width: 26px;
      opacity: 1;
      animation: ${pourOut} 0.24s ease-out forwards;
    `}
`;

const Droplet = styled.span`
  position: absolute;
  right: ${({ $right }) => $right};
  bottom: ${({ $bottom }) => $bottom};
  width: ${({ $size }) => $size};
  height: ${({ $size }) => $size};
  border-radius: 50% 50% 60% 40%;
  background: rgba(205, 239, 189, 0.95);
  opacity: 0;
  transform: scale(0.7);
  pointer-events: none;
  transition: all 0.25s ease;

  ${({ $open, $delay }) =>
    $open &&
    css`
      opacity: 1;
      transform: scale(1);
      transition-delay: ${$delay};
    `}
`;

const NavLinks = styled.div`
  display: flex;
  gap: 1.35rem;
  align-items: center;

  @media (max-width: 768px) {
    position: absolute;
    top: 72px;
    left: 0;
    width: 100%;
    background: linear-gradient(180deg, #8b2231 0%, #741b28 100%);
    flex-direction: column;
    align-items: flex-start;
    padding: ${({ $open }) => ($open ? '1rem 1.25rem 1.25rem' : '0 1.25rem')};
    max-height: ${({ $open }) => ($open ? '340px' : '0')};
    overflow: hidden;
    transition:
      max-height 0.32s ease,
      padding 0.26s ease,
      box-shadow 0.26s ease;
    box-shadow: ${({ $open }) =>
      $open ? '0 18px 30px rgba(0,0,0,0.16)' : 'none'};
    border-top: ${({ $open }) =>
      $open ? '1px solid rgba(255,255,255,0.08)' : 'none'};
  }
`;

const NavLink = styled(Link)`
  color: #fff;
  font-weight: 500;
  position: relative;
  transition: opacity 0.2s ease, transform 0.2s ease;

  &:hover {
    opacity: 0.88;
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 0.9rem 0;
    border-bottom: 1px solid rgba(255,255,255,0.12);
    font-size: 1.05rem;
  }
`;

function SpritzerGlass({ open }) {
  return (
    <IconWrap $open={open}>
      <IconSvg viewBox="0 0 42 58" aria-hidden="true">
        <defs>
          <linearGradient id="spritzerLiquid" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#eef9e6" />
            <stop offset="35%" stopColor="#d7efc9" />
            <stop offset="70%" stopColor="#badf9f" />
            <stop offset="100%" stopColor="#8fc87a" />
          </linearGradient>

          <clipPath id="liquidClip">
            <path d="M10 9 H32 L29.5 29.5 C29.1 32.7 26.4 35 23.1 35 H18.9 C15.6 35 12.9 32.7 12.5 29.5 Z" />
          </clipPath>
        </defs>

        <path
          d="M10 9 H32 L29.5 29.5 C29.1 32.7 26.4 35 23.1 35 H18.9 C15.6 35 12.9 32.7 12.5 29.5 Z"
          fill="rgba(255,255,255,0.05)"
          stroke="rgba(255,255,255,0.96)"
          strokeWidth="2.2"
          strokeLinejoin="round"
        />

        <g clipPath="url(#liquidClip)">
          <rect
            x="8"
            y={open ? '31' : '18'}
            width="26"
            height={open ? '4' : '17'}
            fill="url(#spritzerLiquid)"
            opacity={open ? '0.72' : '1'}
            style={{
              transition: 'all 0.45s ease'
            }}
          />
          <ellipse
            cx="21"
            cy={open ? '31.4' : '18.2'}
            rx="12.4"
            ry="2.8"
            fill="rgba(248,255,244,0.78)"
            opacity={open ? '0.3' : '0.95'}
            style={{
              transition: 'all 0.45s ease'
            }}
          />
        </g>

        <rect x="19.2" y="35.1" width="3.6" height="10.6" rx="2" />
        <ellipse cx="21" cy="48.8" rx="9" ry="2.6" />
      </IconSvg>

      <GlassShine />
      <ShineSweep $open={open} />

      <Bubble $open={open} $left="13px" $bottom="19px" $size="4px" $duration="1.6s" $delay="0s" />
      <Bubble $open={open} $left="19px" $bottom="22px" $size="3px" $duration="1.9s" $delay="0.35s" />
      <Bubble $open={open} $left="24px" $bottom="17px" $size="4px" $duration="2.1s" $delay="0.7s" />
      <Bubble $open={open} $left="17px" $bottom="14px" $size="2.5px" $duration="1.7s" $delay="1s" />

      <Splash $open={open} />
      <Droplet $open={open} $delay="0.08s" $right="-8px" $bottom="15px" $size="5px" />
      <Droplet $open={open} $delay="0.14s" $right="-3px" $bottom="9px" $size="4px" />
      <Droplet $open={open} $delay="0.18s" $right="3px" $bottom="13px" $size="3px" />
    </IconWrap>
  );
}

function Navbar() {
  const [open, setOpen] = useState(false);

  const closeMenu = () => setOpen(false);
  const toggleMenu = () => setOpen(prev => !prev);

  return (
    <Nav>
      <NavInner>
        <Logo to="/" onClick={closeMenu}>
          Stadtheuriger zum Brückl
        </Logo>

        <MenuButton
          onClick={toggleMenu}
          aria-label={open ? 'Menü schließen' : 'Menü öffnen'}
          aria-expanded={open}
          aria-controls="mobile-navigation"
        >
          <SpritzerGlass open={open} />
        </MenuButton>

        <NavLinks id="mobile-navigation" $open={open}>
          <NavLink to="/" onClick={closeMenu}>Start</NavLink>
          <NavLink to="/speisekarte" onClick={closeMenu}>Speisekarte</NavLink>
          <NavLink to="/reservierung" onClick={closeMenu}>Reservierung</NavLink>
          <NavLink to="/kontakt" onClick={closeMenu}>Kontakt</NavLink>
        </NavLinks>
      </NavInner>
    </Nav>
  );
}

export default Navbar;